import React, { useState, useContext, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { DirectionContext } from "../App";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";
import { useLocation } from "react-router-dom";
import Select from "react-select";

// 진동 애니메이션
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`;

// 폼 섹션 애니메이션 variants 추가
const formVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
  }),
};

const formContainerVariants = {
  initial: {
    height: "auto",
    opacity: 0,
  },
  animate: {
    height: "auto",
    opacity: 1,
    transition: {
      height: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.2 },
    },
  },
  exit: {
    height: "auto",
    opacity: 0,
  },
};

const formContentVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 500 : -500,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      x: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.3 },
    },
  },
  exit: (direction) => ({
    x: direction < 0 ? 500 : -500,
    opacity: 0,
    transition: {
      x: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.3 },
    },
  }),
};

// 아이템 애니메이션 추가
const itemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: { duration: 0.2 },
  },
};

// 리스트 아이템 애니메이션 (더 확실한 애니메이션 효과)
const listItemVariants = {
  initial: {
    opacity: 0,
    x: 50,
    scale: 0.8,
  },
  animate: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 15,
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    x: -50,
    scale: 0.8,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

// 폼 컨테이너 애니메이션
const formContainerAnimation = {
  initial: {
    opacity: 0,
    height: 0,
    overflow: "hidden",
  },
  animate: {
    opacity: 1,
    height: "auto",
    transition: {
      duration: 0.5,
      ease: "easeInOut",
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.4,
      ease: "easeInOut",
    },
  },
};

// 모달 관련 스타일 컴포넌트 추가
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled(motion.div)`
  background-color: white;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #00285b;
`;

const ModalContent = styled.p`
  font-size: 15px;
  line-height: 1.5;
  color: #333;
  margin-bottom: 24px;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ModalButton = styled.button`
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const ModalCancelButton = styled(ModalButton)`
  background-color: #f1f1f1;
  color: #666;
`;

const ModalConfirmButton = styled(ModalButton)`
  background-color: #0075ff;
  color: white;
`;

// 은행 목록 데이터
const bankOptions = [
  { value: "KB", label: "KB국민은행", icon: "/bank-icons/kb.png" },
  { value: "shinhan", label: "신한은행", icon: "/bank-icons/shinhan.png" },
  { value: "woori", label: "우리은행", icon: "/bank-icons/woori.png" },
  { value: "smk", label: "새마을금고", icon: "/bank-icons/smk.png" },
  { value: "hana", label: "하나은행", icon: "/bank-icons/hana.png" },
  { value: "nh", label: "농협은행", icon: "/bank-icons/nh.png" },
  { value: "sh", label: "신협은행", icon: "/bank-icons/sh.png" },
  { value: "ibk", label: "기업은행", icon: "/bank-icons/ibk.png" },
  { value: "sc", label: "SC제일은행", icon: "/bank-icons/sc.png" },
  { value: "kakao", label: "카카오뱅크", icon: "/bank-icons/kakao.png" },
  { value: "toss", label: "토스뱅크", icon: "/bank-icons/toss.png" },
  { value: "kdb", label: "산업은행", icon: "/bank-icons/kdb.png" },
  { value: "city", label: "한국씨티은행", icon: "/bank-icons/city.png" },
  { value: "dgb", label: "대구은행", icon: "/bank-icons/dgb.png" },
  { value: "bnk", label: "부산은행", icon: "/bank-icons/bnk.png" },
  { value: "kjb", label: "광주은행", icon: "/bank-icons/kjb.png" },
  { value: "knb", label: "경남은행", icon: "/bank-icons/bnk.png" },
  { value: "kbank", label: "케이뱅크", icon: "/bank-icons/kbank.png" },
  { value: "suhyup", label: "Sh수협은행", icon: "/bank-icons/suhyup.png" },
  { value: "jeju", label: "제주은행", icon: "/bank-icons/jeju.png" },
  { value: "post", label: "우체국", icon: "/bank-icons/post.png" },
];

// 커스텀 옵션 컴포넌트
const CustomOption = ({ innerProps, label, data }) => (
  <OptionContainer {...innerProps}>
    <BankIcon src={data.icon} alt={label} />
    {label}
  </OptionContainer>
);

// 커스텀 단일 값 컴포넌트
const CustomSingleValue = ({ children, data }) => (
  <SingleValueContainer>
    <BankIcon src={data.icon} alt={children} />
    {children}
  </SingleValueContainer>
);

const CoOwnerDataCollection = () => {
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const { navigateTo } = useContext(DirectionContext);
  const location = useLocation();
  const [redirectPath, setRedirectPath] = useState(null);

  // 공동 소유자 상태 관리 (이름과 연락처만)
  const [coOwners, setCoOwners] = useState([
    {
      id: 1,
      name: "",
      phone: "",
    },
  ]);
  const [currentCoOwnerIndex, setCurrentCoOwnerIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(Array(10).fill(false));

  const [form, setForm] = useState({
    name: "",
    phone: "",
    birthDate: "", // 생년월일 (서버에 보내지 않음, 로컬 검증용)
  });

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const [errors, setErrors] = useState({
    name: "",
    phone: "",
    birthDate: "",
  });

  const [shakingFields, setShakingFields] = useState({
    name: false,
    phone: false,
    birthDate: false,
  });

  const nameRef = useRef(null);
  const phoneRef = useRef(null);
  const birthDateRef = useRef(null);

  // 상태 추가
  const [editMode, setEditMode] = useState(Array(10).fill(true)); // 기본적으로 모든 폼은 편집 모드
  const [sameAddressStates, setSameAddressStates] = useState(
    Array(10).fill(false)
  );

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // 외국인 알림을 위한 별도의 토스트 상태
  const [showToast2, setShowToast2] = useState(false);
  const [toastMessage2, setToastMessage2] = useState("");

  // 대표 신청자 번호 모달 관련 상태 추가
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [isRepresentativePhone, setIsRepresentativePhone] = useState(false);

  // 결제 완료 모달 관련 상태 추가
  const [showPaymentCompletedModal, setShowPaymentCompletedModal] =
    useState(false);

  // 외국인 알림 토스트 메시지 표시 함수
  const showForeignerToast = () => {
    setToastMessage2("외국인은 접수를 받지 않습니다");
    setShowToast2(true);
    setTimeout(() => {
      setShowToast2(false);
    }, 3000);
  };

  // 영어 문자 확인 함수
  const containsEnglish = (text) => {
    return /[a-zA-Z]/.test(text);
  };

  // 대표 신청자 주소 저장
  const [representativeAddress, setRepresentativeAddress] = useState(null);

  // 대표 신청자 주소 가져오기
  useEffect(() => {
    try {
      const savedAddress = Cookies.get("representativeAddress");
      if (savedAddress) {
        const addressData = JSON.parse(savedAddress);
        setRepresentativeAddress(addressData);
      }
    } catch (error) {
      console.error("대표 신청자 주소 불러오기 실패:", error);
    }
  }, []);

  // 카카오 주소 API 스크립트 로드
  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // URL 파라미터에서 redirect 값 추출
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirect = searchParams.get("redirect");
    if (redirect) {
      setRedirectPath(`/${redirect}`);
    }
  }, [location]);

  // 페이지 로드 시 스크롤 초기화
  useEffect(() => {
    forceScrollTop();
  }, []);

  // 현재 공동 소유자 정보가 변경될 때마다 폼 업데이트
  // 주의: coOwners는 의존성에서 제외 (handleChange에서 coOwners를 업데이트할 때 form.birthDate가 초기화되는 것을 방지)
  useEffect(() => {
    if (coOwners[currentCoOwnerIndex]) {
      const currentOwner = coOwners[currentCoOwnerIndex];
      // 새 공동 소유자(이름과 전화번호가 모두 빈 문자열)로 이동할 때는 생년월일 초기화
      const isNewOwner = !currentOwner.name && !currentOwner.phone;

      setForm((prevForm) => ({
        ...currentOwner,
        birthDate: isNewOwner ? "" : prevForm.birthDate || "", // 새 공동 소유자면 초기화, 아니면 기존 값 유지
      }));
    } else {
      setForm({
        name: "",
        phone: "",
        birthDate: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCoOwnerIndex]);

  // 모든 가능한 스크롤 위치를 0으로 강제 설정하는 함수
  const forceScrollTop = () => {
    // 메인 문서 요소들의 스크롤 초기화
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // iOS/Safari 대응
    if (window.pageYOffset !== undefined) {
      window.scrollTo(0, 0);
    }

    // 컨텐츠 래퍼 스크롤 초기화
    const contentWrapper = document.querySelector(".content-wrapper");
    if (contentWrapper) {
      contentWrapper.scrollTop = 0;
    }

    // 추가 스크롤 초기화를 위해 지연 실행
    setTimeout(() => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      window.scrollTo(0, 0);
    }, 50);
  };

  // 다음 주소 검색 실행 함수
  const openPostcodeSearch = () => {
    // 기존 container가 있다면 제거
    const existingContainer = document.getElementById("postcode-container");
    if (existingContainer) {
      existingContainer.remove();
    }

    // 새 container 생성
    const postcodeContainer = document.createElement("div");
    postcodeContainer.id = "postcode-container";
    postcodeContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
    `;

    // 내부 컨테이너 생성
    const innerContainer = document.createElement("div");
    innerContainer.style.cssText = `
      position: relative;
      width: 90%;
      max-width: 500px;
      height: 500px;
      background-color: white;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;

    // 내부 컨테이너 클릭 시 이벤트 버블링 방지
    innerContainer.onclick = function (e) {
      e.stopPropagation();
    };

    postcodeContainer.appendChild(innerContainer);
    document.body.appendChild(postcodeContainer);

    // 외부 영역 클릭 시 모달 닫기
    postcodeContainer.onclick = function () {
      document.body.removeChild(postcodeContainer);
    };

    new window.daum.Postcode({
      oncomplete: function (data) {
        // 도로명 주소 또는 지번 주소 선택
        let addr = data.roadAddress || data.jibunAddress;

        // 주소 및 우편번호 필드 업데이트
        handleChange("address")(addr);
        handleChange("postcode")(data.zonecode || "");

        // 주소 검색 완료 (더 이상 상세주소 필드가 없으므로 포커스 제거)

        // 검색 완료 후 컨테이너 제거
        document.body.removeChild(postcodeContainer);
      },
      // 검색창 크기 및 UI 설정
      width: "100%",
      height: "100%",
      maxSuggestItems: 5,
      theme: {
        searchBgColor: "#FFFFFF", // 검색창 배경색을 흰색으로 변경
        queryTextColor: "#333333", // 검색창 텍스트 색상을 어두운 색으로 변경
        emphTextColor: "#0075FF", // 강조 텍스트 색상
        outlineColor: "#e0e0e0", // 테두리 색상
      },
      alwaysShowEngAddr: false,
      hideMapBtn: true,
      hideEngBtn: true,
      shorthand: true,
      animation: true,
    }).embed(innerContainer);
  };

  const handleChange = (key) => (e) => {
    let value = e;

    // 이벤트 객체인 경우 값 추출
    if (e && e.target && e.target.value !== undefined) {
      value = e.target.value;
    }

    // 이름 필드에 영어 입력 감지 및 처리
    if (key === "name" && containsEnglish(value)) {
      showForeignerToast(); // 외국인 알림 토스트 표시
      value = value.replace(/[a-zA-Z]/g, ""); // 영어 문자 제거
    }

    // 전화번호는 숫자만 입력 가능하게 처리
    if (key === "phone") {
      value = value.replace(/[^0-9]/g, "").slice(0, 11);

      // 11자리가 다 채워졌을 때 생년월일 인풋으로 포커스 이동
      if (value.length === 11 && birthDateRef.current) {
        setTimeout(() => {
          birthDateRef.current?.focus();
        }, 100);
      }
    }

    // 생년월일은 숫자만 입력 가능하게 처리 (8자리)
    if (key === "birthDate") {
      value = value.replace(/[^0-9]/g, "").slice(0, 8);

      // 8자리 입력되면 유효성 검증
      if (value.length === 8) {
        const validationError = validateBirthDate(value);
        if (validationError) {
          setToastMessage2(validationError);
          setShowToast2(true);
          setTimeout(() => {
            setShowToast2(false);
          }, 3000);
          // 생년월일 초기화
          value = "";
          if (birthDateRef.current) {
            birthDateRef.current.blur();
          }
          // 에러 메시지 설정
          setErrors((prev) => ({ ...prev, birthDate: validationError }));
        } else {
          // 유효한 생년월일이면 키보드 닫기 및 에러 메시지 초기화
          if (birthDateRef.current) {
            birthDateRef.current.blur();
          }
          // 에러 메시지 초기화
          setErrors((prev) => ({ ...prev, birthDate: "" }));
        }
      } else {
        // 8자리가 아니면 에러 메시지 초기화 (입력 중)
        if (errors.birthDate) {
          setErrors((prev) => ({ ...prev, birthDate: "" }));
        }
      }
    }

    setForm((prevForm) => ({ ...prevForm, [key]: value }));

    // 현재 공동 소유자 데이터 업데이트 (birthDate는 제외 - 서버에 보내지 않음)
    setCoOwners((prevOwners) => {
      const updatedOwners = [...prevOwners];
      const updatedOwner = { ...updatedOwners[currentCoOwnerIndex] };

      // birthDate는 coOwners 배열에 저장하지 않음 (로컬 검증용으로만 사용)
      if (key !== "birthDate") {
        updatedOwner[key] = value;
      }

      updatedOwners[currentCoOwnerIndex] = updatedOwner;
      return updatedOwners;
    });

    // 입력 중에는 에러 메시지 초기화 (생년월일은 위에서 이미 처리했으므로 제외)
    if (key !== "birthDate" && errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  // 이름 유효성 검사
  const validateName = (name) => {
    if (!name || name.trim().length < 2) {
      return "이름을 입력해주세요";
    }
    if (!/^[가-힣\s]+$/.test(name)) {
      return "한글 이름만 입력 가능합니다";
    }
    return "";
  };

  // 생년월일 유효성 검사 (IdentityVerificationInputPage.js의 validateBirthDate 로직 참고)
  const validateBirthDate = (birthDateStr) => {
    if (!birthDateStr || birthDateStr.length !== 8) {
      return "생년월일을 8자리로 입력해주세요";
    }

    const year = parseInt(birthDateStr.substring(0, 4));
    const month = parseInt(birthDateStr.substring(4, 6));
    const day = parseInt(birthDateStr.substring(6, 8));

    // 연도 유효성 검증 (1900년 이후, 현재 연도 이하)
    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear) {
      return "올바른 생년월일을 입력해주세요";
    }

    // 월 유효성 검증 (1-12)
    if (month < 1 || month > 12) {
      return "올바른 생년월일을 입력해주세요";
    }

    // 일 유효성 검증
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
      return "올바른 생년월일을 입력해주세요";
    }

    // 미래 날짜 체크
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (birthDate > today) {
      return "미래 날짜는 입력할 수 없습니다";
    }

    // 미성년자 체크 (현재 연도 기준으로 19세 미만이면 접수 불가)
    const age = currentYear - year;
    // 생일이 지났는지 확인
    const currentMonth = new Date().getMonth() + 1;
    const currentDay = new Date().getDate();
    const actualAge =
      currentMonth > month || (currentMonth === month && currentDay >= day)
        ? age
        : age - 1;

    if (actualAge < 19) {
      return "미성년자는 접수를 받지 않습니다";
    }

    return "";
  };

  // 전화번호 유효성 검사
  const validatePhone = (phone) => {
    const phoneRegex = /^01[0-9]{8,9}$/;
    if (!phoneRegex.test(phone)) {
      return "올바른 휴대폰 번호 형식이 아닙니다.";
    }
    return "";
  };

  // 이메일 유효성 검사
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!emailRegex.test(email)) {
      return "올바른 이메일 주소 형식이 아닙니다.";
    }
    return "";
  };

  // 계좌번호 유효성 검사
  const validateAccountNumber = (accountNumber) => {
    const accountRegex = /^[0-9-]+$/;
    if (!accountRegex.test(accountNumber)) {
      return "계좌번호는 숫자만 입력 가능합니다.";
    }
    return "";
  };

  // 예금주명 유효성 검사
  const validateAccountHolder = (accountHolder) => {
    if (!accountHolder || accountHolder.trim().length < 2) {
      return "예금주명을 정확히 입력해주세요.";
    }
    // 공백을 모두 제거하고 비교
    const cleanAccountHolder = accountHolder.replace(/\s+/g, "");
    const cleanName = form.name.replace(/\s+/g, "");
    if (cleanAccountHolder !== cleanName) {
      return "예금주명은 입력하신 이름과 동일해야 합니다.";
    }
    return "";
  };

  // 진동 효과 실행 후 초기화
  useEffect(() => {
    // 진동 효과가 있는 필드 확인
    const hasShakingField = Object.values(shakingFields).some((val) => val);

    if (hasShakingField) {
      const timer = setTimeout(() => {
        setShakingFields({
          name: false,
          phone: false,
          birthDate: false,
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shakingFields]);

  // 현재 공동 소유자 정보 유효성 검사 (이름, 연락처, 생년월일)
  const validateCurrentCoOwner = () => {
    const nameError = validateName(form.name);
    const phoneError = validatePhone(form.phone);
    const birthDateError = validateBirthDate(form.birthDate);
    let isValid = true;

    setErrors({
      name: nameError,
      phone: phoneError,
      birthDate: birthDateError,
    });

    if (nameError) {
      if (nameRef.current) nameRef.current.focus();
      setShakingFields({ ...shakingFields, name: true });
      isValid = false;
    } else if (phoneError) {
      if (phoneRef.current) phoneRef.current.focus();
      setShakingFields({ ...shakingFields, phone: true });
      isValid = false;
    } else if (birthDateError) {
      if (birthDateRef.current) birthDateRef.current.focus();
      setShakingFields({ ...shakingFields, birthDate: true });
      isValid = false;
    }

    return isValid;
  };

  // 현재 공동 소유자 입력 완료 여부 확인 (이름, 연락처, 생년월일 8자리)
  const isCurrentCoOwnerFilled = () => {
    const nameValid = form.name?.trim() !== "";
    const phoneValid = form.phone?.trim() !== "";
    const birthDateLengthValid = form.birthDate?.length === 8;

    // 생년월일이 8자리일 때만 유효성 검증 수행
    let birthDateValid = false;
    if (birthDateLengthValid) {
      const birthDateError = validateBirthDate(form.birthDate);
      birthDateValid = birthDateError === ""; // 에러가 없으면 유효
    }
    return nameValid && phoneValid && birthDateLengthValid && birthDateValid;
  };

  // 공동 소유자 완료 처리 (이름과 연락처만)
  const handleCoOwnerComplete = async () => {
    const isValid = validateCurrentCoOwner();
    if (isValid) {
      // 사용자 조회 API 호출
      try {
        const queryParams = new URLSearchParams({
          phone: form.phone,
        });

        const response = await fetch(
          `${
            API_ENDPOINTS.BASE_URL
          }/applications/status-check?${queryParams.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        // API 응답 처리
        if (response.ok) {
          const data = await response.json();

          // success: false이고 메시지가 "공동신청인이 접수를 완료(결제 완료)한 상태입니다"이면 모달 표시
          if (
            data.success === false &&
            data.message === "공동신청인이 접수를 완료(결제 완료)한 상태입니다"
          ) {
            setShowPaymentCompletedModal(true);
            return; // 함수 종료하여 나머지 로직은 모달 응답 후 처리
          }

          // success: true이고 메시지가 "공동신청인이 접수를 완료(결제 완료)한 상태가 아닙니다"이면 정상 진행
          // 이 경우는 아래 로직 계속 진행
        } else {
          // API 호출 실패해도 계속 진행
        }
      } catch (error) {
        console.error("사용자 조회 API 호출 실패:", error);
        // API 호출 실패해도 계속 진행
      }

      const updatedCoOwners = [...coOwners];
      // birthDate는 서버에 보내지 않으므로 제외하고 저장
      const { birthDate, ...ownerData } = form;
      updatedCoOwners[currentCoOwnerIndex] = ownerData;

      // 쿠키에서 대표 신청자 전화번호 가져오기
      const verifiedPhoneNumber = Cookies.get("verifiedPhoneNumber");

      // 현재 입력한 전화번호가 대표 신청자 번호와 일치하는지 확인
      if (verifiedPhoneNumber && form.phone === verifiedPhoneNumber) {
        // 일치하면 모달 표시
        setIsRepresentativePhone(true);
        setShowPhoneModal(true);
        return; // 함수 종료하여 나머지 로직은 모달 응답 후 처리
      }

      // 나머지 코드 (전화번호가 일치하지 않는 경우)
      setCoOwners(updatedCoOwners);

      // 해당 인덱스를 완료로 표시
      const newCompleted = [...isCompleted];
      newCompleted[currentCoOwnerIndex] = true;
      setIsCompleted(newCompleted);

      // 편집 모드 종료
      const newEditMode = [...editMode];
      newEditMode[currentCoOwnerIndex] = false;
      setEditMode(newEditMode);
    }
  };

  // 수정 모드로 전환하는 함수 추가
  const handleEditMode = () => {
    // 편집 모드 활성화
    const newEditMode = [...editMode];
    newEditMode[currentCoOwnerIndex] = true;
    setEditMode(newEditMode);
  };

  // 대표 신청자 전화번호 모달 확인 처리
  const handlePhoneModalConfirm = () => {
    setShowPhoneModal(false);

    // 현재 폼 초기화
    setForm({
      ...form,
      phone: "", // 전화번호만 초기화
      birthDate: "", // 생년월일도 초기화
    });

    // coOwners 배열도 업데이트 (birthDate는 제외)
    const updatedCoOwners = [...coOwners];
    updatedCoOwners[currentCoOwnerIndex] = {
      ...updatedCoOwners[currentCoOwnerIndex],
      phone: "",
    };
    setCoOwners(updatedCoOwners);

    // 전화번호 필드에 포커스
    if (phoneRef.current) {
      setTimeout(() => {
        phoneRef.current.focus();
      }, 100);
    }
  };

  // 대표 신청자 전화번호 모달 취소 처리
  const handlePhoneModalCancel = () => {
    setShowPhoneModal(false);

    // 일반적인 완료 처리 진행 (birthDate는 제외)
    const updatedCoOwners = [...coOwners];
    const { birthDate, ...ownerData } = form;
    updatedCoOwners[currentCoOwnerIndex] = ownerData;
    setCoOwners(updatedCoOwners);

    // 해당 인덱스를 완료로 표시
    const newCompleted = [...isCompleted];
    newCompleted[currentCoOwnerIndex] = true;
    setIsCompleted(newCompleted);

    // 편집 모드 종료
    const newEditMode = [...editMode];
    newEditMode[currentCoOwnerIndex] = false;
    setEditMode(newEditMode);
  };

  // 결제 완료 모달 확인 처리
  const handlePaymentCompletedModalConfirm = () => {
    setShowPaymentCompletedModal(false);

    // 현재 폼 초기화
    setForm({
      ...form,
      name: "",
      phone: "",
      birthDate: "",
    });

    // coOwners 배열도 업데이트 (birthDate는 제외)
    const updatedCoOwners = [...coOwners];
    updatedCoOwners[currentCoOwnerIndex] = {
      ...updatedCoOwners[currentCoOwnerIndex],
      name: "",
      phone: "",
    };
    setCoOwners(updatedCoOwners);

    // 이름 필드에 포커스
    if (nameRef.current) {
      setTimeout(() => {
        nameRef.current.focus();
      }, 100);
    }
  };

  // 공동 소유자 추가 (애니메이션 강화)
  const handleAddCoOwner = () => {
    // 최대 10명까지만 추가 가능
    if (coOwners.length < 10) {
      const newCoOwnerId = coOwners.length + 1;

      // 새 공동 소유자 객체 생성 (이름과 연락처만, birthDate는 서버에 보내지 않음)
      const newCoOwner = {
        id: newCoOwnerId,
        name: "",
        phone: "",
      };

      // 애니메이션을 위한 딜레이 추가
      setTimeout(() => {
        setCoOwners((prev) => [...prev, newCoOwner]);

        // 새 소유자 추가 시 편집 모드로 설정
        const newEditMode = [...editMode];
        newEditMode[coOwners.length] = true;
        setEditMode(newEditMode);

        // 새로 추가된 소유자로 이동 (useEffect가 실행되어 생년월일이 자동으로 초기화됨)
        setCurrentCoOwnerIndex(coOwners.length);

        // 스크롤 맨 위로
        forceScrollTop();
      }, 50);

      // 자동으로 새 항목으로 스크롤
      setTimeout(() => {
        const scrollContainer = document.querySelector(
          ".co-owner-scroll-container"
        );
        if (scrollContainer) {
          scrollContainer.scrollTo({
            left: scrollContainer.scrollWidth,
            behavior: "smooth",
          });
        }
      }, 200);
    }
  };

  // 이전 소유자와 동일한 주소 사용
  const [useSameAddress, setUseSameAddress] = useState(false);

  // 이전 소유자의 주소를 가져오는 함수
  const getPreviousOwnerAddress = () => {
    if (representativeAddress) {
      // 대표 신청자 주소가 있으면 그것을 반환
      return representativeAddress;
    } else if (currentCoOwnerIndex > 0) {
      // 첫 번째 이전 소유자를 찾음 (완료된 소유자여야 함)
      for (let i = currentCoOwnerIndex - 1; i >= 0; i--) {
        if (isCompleted[i]) {
          return {
            address: coOwners[i].address,
            postcode: coOwners[i].postcode || "",
            detailAddress: coOwners[i].detailAddress,
          };
        }
      }
    }
    return { address: "", postcode: "", detailAddress: "" };
  };

  // 주소 동일 체크박스 핸들러
  const handleSameAddressChange = (e) => {
    const checked = e.target.checked;
    setUseSameAddress(checked);

    // 체크박스 상태 저장
    const newSameAddressStates = [...sameAddressStates];
    newSameAddressStates[currentCoOwnerIndex] = checked;
    setSameAddressStates(newSameAddressStates);

    if (checked) {
      // 체크박스 선택 시 이전 소유자의 주소 사용
      const { address, postcode, detailAddress } = getPreviousOwnerAddress();
      handleChange("address")(address);
      handleChange("postcode")(postcode);
      handleChange("detailAddress")(detailAddress);
    } else {
      // 체크박스 해제 시 주소 정보 비우기
      handleChange("address")("");
      handleChange("detailAddress")("");
    }
  };

  // 공동 소유자 삭제
  const handleDeleteCoOwner = (indexToDelete) => {
    if (coOwners.length > 1) {
      // 삭제 전 coOwners 배열의 길이 저장
      const prevLength = coOwners.length;

      // 새로운 인덱스 계산
      let newIndex = indexToDelete;

      // 삭제하려는 항목이 마지막 항목이면 이전 항목으로 이동
      if (indexToDelete === prevLength - 1) {
        newIndex = indexToDelete - 1;
      }
      // 삭제하려는 항목이 첫 번째 항목이 아니면서 현재 선택된 항목이면 이전 항목으로 이동
      else if (indexToDelete === currentCoOwnerIndex && indexToDelete > 0) {
        newIndex = indexToDelete - 1;
      }
      // 삭제하려는 항목이 현재 선택된 항목보다 앞에 있으면 인덱스 조정
      else if (indexToDelete < currentCoOwnerIndex) {
        newIndex = currentCoOwnerIndex - 1;
      }

      // 먼저 배열 업데이트
      const updatedCoOwners = coOwners.filter(
        (_, index) => index !== indexToDelete
      );
      const updatedCompleted = isCompleted.filter(
        (_, index) => index !== indexToDelete
      );
      const updatedEditMode = editMode.filter(
        (_, index) => index !== indexToDelete
      );
      const updatedSameAddressStates = sameAddressStates.filter(
        (_, index) => index !== indexToDelete
      );

      // 상태 업데이트
      setCoOwners(updatedCoOwners);
      setIsCompleted(updatedCompleted);
      setEditMode(updatedEditMode);
      setSameAddressStates(updatedSameAddressStates);

      // 인덱스 업데이트 (다음 렌더링 사이클에서 실행)
      setTimeout(() => {
        setCurrentCoOwnerIndex(newIndex);
      }, 0);

      // 삭제 후 스크롤 조정
      setTimeout(() => {
        const scrollContainer = document.querySelector(
          ".co-owner-scroll-container"
        );
        const currentItem = document.querySelector(
          `.co-owner-item-${newIndex}`
        );

        if (scrollContainer && currentItem) {
          scrollContainer.scrollTo({
            left: currentItem.offsetLeft - 40,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  // 공동 소유자 선택
  const handleSelectCoOwner = (index) => {
    setCurrentCoOwnerIndex(index);
    // coOwners 배열에는 birthDate가 없으므로 빈 문자열로 초기화
    setForm({
      ...coOwners[index],
      birthDate: "", // 공동 소유자를 선택할 때마다 생년월일 초기화
    });

    // 선택된 항목이 스크롤 컨테이너에서 보이도록 스크롤
    setTimeout(() => {
      const scrollContainer = document.querySelector(
        ".co-owner-scroll-container"
      );
      const selectedItem = document.querySelector(`.co-owner-item-${index}`);

      if (scrollContainer && selectedItem) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const itemRect = selectedItem.getBoundingClientRect();

        // 아이템이 컨테이너 밖에 있는 경우 스크롤
        if (
          itemRect.left < containerRect.left ||
          itemRect.right > containerRect.right
        ) {
          scrollContainer.scrollTo({
            left:
              selectedItem.offsetLeft -
              containerRect.width / 2 +
              itemRect.width / 2,
            behavior: "smooth",
          });
        }
      }
    }, 100);
  };

  // 최종 제출 처리
  const handleSubmit = async (e) => {
    // 중요: 이벤트 기본 동작 방지
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // 완성된 공동 소유자 데이터만 필터링
    const completedOwners = coOwners.filter((_, index) => isCompleted[index]);

    // 최종 유효성 검사 수행 (이름과 연락처만)
    let hasErrors = false;
    completedOwners.forEach((owner) => {
      // 이름 검사
      const nameError = validateName(owner.name);
      if (nameError) {
        setErrors((prev) => ({ ...prev, name: nameError }));
        hasErrors = true;
      }

      // 전화번호 검사
      const phoneError = validatePhone(owner.phone);
      if (phoneError) {
        setErrors((prev) => ({ ...prev, phone: phoneError }));
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setToastMessage("입력하신 정보를 다시 확인해주세요.");
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
      return;
    }

    setIsLoading(true);

    try {
      // 쿠키에 공동 소유자 정보 저장 (빈 값 제외)
      Cookies.set("coOwners", JSON.stringify(completedOwners), { expires: 7 });

      // 본인 포함 총 인원 수 계산 (본인 1명 + 공동신청자 수)
      const totalApplicants = 1 + completedOwners.length;
      Cookies.set("totalApplicants", totalApplicants.toString(), {
        expires: 7,
        path: "/",
      });

      // 다음 페이지로 이동
      navigateTo(redirectPath || "/screenshot");
      return true;
    } catch (error) {
      console.error("오류 발생:", error);
      setToastMessage("데이터 저장 중 오류가 발생했습니다");
      setShowToast(true);

      setTimeout(() => {
        setShowToast(false);
      }, 3000);

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // 페이지 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
      },
    },
  };

  // 공동 소유자 추가 버튼 활성화 조건
  const canAddNewCoOwner = () => {
    // 모든 공동 소유자가 완료되었는지 확인
    const allCompleted = coOwners.every(
      (owner, index) =>
        !owner.name || owner.name.trim() === "" || isCompleted[index]
    );

    // 10명 제한 확인
    const underLimit = coOwners.length < 10;

    // 적어도 한 명의 완료된 공동 소유자가 있는지 확인
    const hasCompletedOwner = isCompleted.some((completed) => completed);

    return allCompleted && underLimit && hasCompletedOwner;
  };

  // 토스트 애니메이션
  const toastVariants = {
    initial: {
      opacity: 0,
      y: 50,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      y: 50,
      transition: {
        duration: 0.3,
      },
    },
  };

  // 두 번째 토스트 애니메이션 (외국인 접수 불가 알림)
  const toast2Variants = {
    initial: {
      opacity: 0,
      y: -80,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      y: -80,
      transition: {
        duration: 0.3,
      },
    },
  };

  // 데이터 컬렉션 초기 설정
  useEffect(() => {
    // 다른 페이지로부터 리디렉션된 경우
    const { state } = location;
    if (state?.redirectPath) {
      setRedirectPath(state.redirectPath);
    }

    // 쿠키에서 대표 신청자 전화번호 가져오기
    const verifiedPhoneNumber = Cookies.get("verifiedPhoneNumber");
    if (verifiedPhoneNumber) {
    }

    // 이전 페이지에서 설정한 쿠키에서 데이터 가져오기
    const savedCoOwners = Cookies.get("coOwners");
    if (savedCoOwners) {
      try {
        const coOwnersData = JSON.parse(savedCoOwners);
        setCoOwners(coOwnersData);
      } catch (error) {
        console.error("오류 발생:", error);
      }
    }
  }, [location]);

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <Header>
            <SubTitle>공동신청인 정보를 입력해주세요</SubTitle>
            <Description>신청 인원 수만큼 반복하여 입력해주세요</Description>
          </Header>

          {/* 안내문구 */}
          <InfoBox>
            <InfoIcon>ⓘ</InfoIcon>
            <InfoText>
              {/* 본 신청은 미성년자와 외국인은 접수받지 않습니다.
              <br /> */}
              원활한 접수를 위해, 공동 신청하시는 분과
              <br /> 합의된 경우에만 정보를 기재해 주십시오.
            </InfoText>
          </InfoBox>

          {/* 공동 소유자 목록 */}
          <CoOwnerListContainer>
            <CoOwnerScrollContainer className="co-owner-scroll-container">
              <AnimatePresence mode="popLayout">
                {coOwners.map((owner, index) => (
                  <CoOwnerItem
                    key={`owner-${index}`}
                    className={`co-owner-item-${index}`}
                    isActive={index === currentCoOwnerIndex}
                    isCompleted={isCompleted[index]}
                    isEditing={editMode[index]}
                    onClick={() => handleSelectCoOwner(index)}
                    as={motion.div}
                    variants={listItemVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    layoutId={`owner-item-${index}`}
                    whileHover={{
                      scale: 1.05,
                      transition: { duration: 0.2 },
                    }}
                    whileTap={{
                      scale: 0.95,
                      transition: { duration: 0.1 },
                    }}
                  >
                    {coOwners.length > 1 && (
                      <ItemDeleteButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCoOwner(index);
                        }}
                      >
                        ×
                      </ItemDeleteButton>
                    )}
                    <CoOwnerName isActive={index === currentCoOwnerIndex}>
                      <NameText>{owner.name || `신청자`}</NameText>
                      {/* {index === currentCoOwnerIndex && !isCompleted[index] && (
                        <StatusText>선택 중</StatusText>
                      )} */}
                      {isCompleted[index] && (
                        <CheckIcon
                          src="/png/CheckSuc.png"
                          alt="완료"
                          as={motion.img}
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{
                            scale: 1,
                            opacity: 1,
                            transition: { delay: 0.2, duration: 0.3 },
                          }}
                        />
                      )}
                    </CoOwnerName>
                  </CoOwnerItem>
                ))}
              </AnimatePresence>
            </CoOwnerScrollContainer>
          </CoOwnerListContainer>

          {/* 신원정보 입력 섹션 */}
          <VerificationSection
            as={motion.div}
            variants={formContainerAnimation}
            initial="initial"
            animate="animate"
            exit="exit"
            key={`form-container-${currentCoOwnerIndex}`}
          >
            {apiError && <ErrorAlert>{apiError}</ErrorAlert>}
            <SectionTitle>신원정보를 입력해주세요</SectionTitle>
            <FormSection
              as={motion.div}
              variants={formContentVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <FormGroup>
                <Label>
                  이름<Required>*</Required>
                </Label>
                <Input
                  type="text"
                  placeholder="이름을 입력하세요"
                  value={form.name}
                  onChange={(e) => {
                    // 입력 중에는 모든 값을 허용
                    handleChange("name")(e);
                  }}
                  onBlur={(e) => {
                    // 포커스를 잃었을 때 한글만 남기고 필터링
                    const filteredName = e.target.value
                      .replace(/[^가-힣\s]/g, "")
                      .trim();
                    handleChange("name")(filteredName);
                  }}
                  ref={nameRef}
                  hasError={!!errors.name}
                  isShaking={shakingFields.name}
                  disabled={!editMode[currentCoOwnerIndex]}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>
                  전화번호<Required>*</Required>
                </Label>
                <Input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="전화번호를 입력해주세요"
                  value={form.phone}
                  onChange={(e) => handleChange("phone")(e)}
                  ref={phoneRef}
                  hasError={!!errors.phone}
                  isShaking={shakingFields.phone}
                  disabled={!editMode[currentCoOwnerIndex]}
                  isNumeric
                />
                {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label>
                  생년월일<Required>*</Required>
                </Label>
                <MinorWarningText>
                  본 신청은 미성년자와 외국인은 접수받지 않습니다.
                </MinorWarningText>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="생년월일을 입력해주세요 (YYYYMMDD)"
                  value={form.birthDate}
                  onChange={(e) => handleChange("birthDate")(e)}
                  ref={birthDateRef}
                  hasError={!!errors.birthDate}
                  isShaking={shakingFields.birthDate}
                  disabled={!editMode[currentCoOwnerIndex]}
                  maxLength={8}
                  isNumeric
                />
                {errors.birthDate && (
                  <ErrorMessage>{errors.birthDate}</ErrorMessage>
                )}
              </FormGroup>

              {/* 버튼 섹션을 폼 내부로 이동 */}
              <ActionButtonsContainer>
                <DeleteButton
                  onClick={() => handleDeleteCoOwner(currentCoOwnerIndex)}
                  disabled={coOwners.length <= 1}
                >
                  <TrashIcon src="/png/trash.png" alt="삭제" />
                  삭제
                </DeleteButton>
                {editMode[currentCoOwnerIndex] ? (
                  <CompleteButton
                    onClick={handleCoOwnerComplete}
                    disabled={!isCurrentCoOwnerFilled()}
                  >
                    작성 완료
                  </CompleteButton>
                ) : (
                  <EditButton
                    onClick={handleEditMode}
                    disabled={!isCompleted[currentCoOwnerIndex]}
                  >
                    수정하기
                  </EditButton>
                )}
              </ActionButtonsContainer>
            </FormSection>
          </VerificationSection>

          {/* 공동 신청인 추가 버튼 */}
          <AddCoOwnerButtonWrapper>
            <AddCoOwnerButton
              onClick={handleAddCoOwner}
              disabled={!canAddNewCoOwner()}
            >
              <PlusIcon src="/png/plus.png" alt="추가" /> 공동 신청인 추가
            </AddCoOwnerButton>
          </AddCoOwnerButtonWrapper>

          <ButtonsContainer>
            <ActionButton
              onClick={(e) => handleSubmit(e)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={
                isLoading || !isCompleted.some((completed) => completed)
              }
            >
              {isLoading ? "처리 중..." : "다음으로"}
            </ActionButton>
          </ButtonsContainer>

          {/* 토스트 알림 */}
          <AnimatePresence>
            {showToast && (
              <Toast
                variants={toastVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <AlertIcon>!</AlertIcon>빈 공동 소유자 폼이 있습니다.
                <br /> 삭제하거나 완료해주세요
              </Toast>
            )}
          </AnimatePresence>

          {/* 외국인 접수 불가 알림 */}
          <AnimatePresence>
            {showToast2 && (
              <Toast2
                variants={toast2Variants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <AlertIcon>!</AlertIcon>
                {toastMessage2}
              </Toast2>
            )}
          </AnimatePresence>

          {/* 대표 신청자 전화번호 모달 */}
          <AnimatePresence>
            {showPhoneModal && (
              <ModalOverlay>
                <ModalContainer
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                  <ModalTitle>대표 신청자 번호 사용 불가</ModalTitle>
                  <ModalContent>
                    입력하신 전화번호는 대표 신청자의 번호입니다. 대표 신청자의
                    번호는 공동 신청자로 사용할 수 없습니다. 다른 전화번호를
                    입력해 주세요.
                  </ModalContent>
                  <ModalButtonGroup>
                    <ModalConfirmButton
                      onClick={handlePhoneModalConfirm}
                      style={{ width: "100%" }}
                    >
                      확인
                    </ModalConfirmButton>
                  </ModalButtonGroup>
                </ModalContainer>
              </ModalOverlay>
            )}
          </AnimatePresence>

          {/* 결제 완료 모달 */}
          <AnimatePresence>
            {showPaymentCompletedModal && (
              <ModalOverlay>
                <ModalContainer
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                  <ModalTitle>접수 불가</ModalTitle>
                  <ModalContent>
                    입력하신 정보의 공동신청인은 이미 접수를 완료(결제 완료)한
                    상태입니다. 다른 공동신청인 정보를 입력해 주세요.
                  </ModalContent>
                  <ModalButtonGroup>
                    <ModalConfirmButton
                      onClick={handlePaymentCompletedModalConfirm}
                      style={{ width: "100%" }}
                    >
                      확인
                    </ModalConfirmButton>
                  </ModalButtonGroup>
                </ModalContainer>
              </ModalOverlay>
            )}
          </AnimatePresence>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 공동 소유자 목록 스타일
const CoOwnerListContainer = styled.div`
  width: 100%;
  margin-bottom: 10px;
  position: relative;
  overflow: hidden;
  padding: 5px 0;
`;

const CoOwnerScrollContainer = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  height: 60px;
  margin: 0 auto;
  overflow-x: auto;
  padding: 5px 0;
  -webkit-overflow-scrolling: touch; /* iOS 스크롤 스무스하게 */
  scroll-behavior: smooth;

  /* 스크롤바 숨김 처리 */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const CoOwnerItem = styled(motion.div)`
  margin-left: 5px;
  position: relative;
  border-radius: 8px;
  padding: 12px 16px;
  border: 0.5px solid
    ${(props) => {
      if (props.isActive && !props.isCompleted) return "#0075FF";
      if (props.isCompleted && !props.isEditing) return "#e5e5e5";
      if (props.isCompleted && props.isEditing) return "#0075FF";
      return "#e5e5e5";
    }};
  background-color: white;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  width: 120px;
  flex: 0 0 auto;
  transition: all 0.3s ease;
  box-shadow: ${(props) =>
    props.isActive && !props.isCompleted
      ? "0 0 0 2px #0075FF"
      : "0 2px 4px rgba(0, 0, 0, 0.05)"};
  will-change: transform, opacity;

  @media (max-width: 480px) {
    width: 120px;
    min-width: 70px;
  }
`;

const ItemDeleteButton = styled.button`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #ff4d4f;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  opacity: 1;
  z-index: 2;
  transition: opacity 0.2s, transform 0.2s;

  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const CoOwnerName = styled.div`
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  text-align: center;
  overflow: hidden;
  padding: 0 2px;
  white-space: nowrap;
  font-size: 15px;
  color: #333;
`;

const NameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;
`;

const StatusText = styled.span`
  color: #0075ff;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
`;

const InputStatusText = styled.span`
  font-size: 11px;
  color: #0075ff;
  margin-top: 4px;
`;

const CheckIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

// 버튼 컨테이너 스타일
const ActionButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 20px;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  color: #dc4b3f;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover {
    text-decoration: underline;
  }
`;

const TrashIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
  margin-right: 6px;
`;

const CompleteButton = styled.button`
  display: flex;
  width: 90px;
  height: 32px;
  padding: 14px 16px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  border-radius: 6px;
  background: ${(props) => (props.disabled ? "#CBCFD5" : "#4CAF50")};
  border: none;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  transition: background-color 0.2s;

  &:hover {
    background: ${(props) => (props.disabled ? "#CBCFD5" : "#419544")};
  }
`;

const EditButton = styled.button`
  display: flex;
  width: 90px;
  height: 32px;
  padding: 14px 16px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  border-radius: 6px;
  background: #0075ff;
  border: none;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover {
    background: #0075ff;
  }
`;

const AddCoOwnerButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

const AddCoOwnerButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  color: #0075ff;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: color 0.2s;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  padding: 0;
  background: none;
  border: none;

  &:hover {
    color: #0066df;
  }
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 0px;
  padding: 0 0px;
  margin: 0 auto;

  @media (max-width: 768px) {
    margin-top: 0px;
    padding-bottom: 40px;
  }

  @media (min-width: 769px) {
    padding: 0;
    max-width: 470px;
  }
`;

const PlusIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

// 추가 스타일
const AddressInputContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
`;

const AddressInput = styled.input`
  flex: 1;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 16px;
  font-size: 16px;
  background-color: ${(props) => (props.disabled ? "#f5f5f5" : "white")};
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};

  &::placeholder {
    color: #999;
  }

  &:focus {
    border-color: #0075ff;
    outline: none;
  }
`;

// 애니메이션 정의
const shakeAnimation = keyframes`
  0% { transform: translateX(0); }
  20% { transform: translateX(-10px); }
  40% { transform: translateX(10px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
  100% { transform: translateX(0); }
`;

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f6f8ff;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #f6f8ff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f6f8ff;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 20px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.4;
`;

const InfoBox = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  box-sizing: border-box;
`;

const InfoIcon = styled.span`
  color: #0075ff;
  margin-top: 10px;
  font-size: 16px;
  margin-right: 0px;
  font-weight: bold;
  flex-shrink: 0;
`;
const InfoIcon2 = styled.span`
  color: #0075ff;
  font-size: 16px;
  margin-right: 0px;
  font-weight: bold;
  flex-shrink: 0;
`;

const InfoText = styled.p`
  font-size: 13px;
  color: #333;
  line-height: 1.5;
  margin: 0;
  flex: 1;
`;

const VerificationSection = styled(motion.div)`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;
  will-change: height;
`;

const FormSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin: 0 0 8px 0;
`;

const FormGroup = styled.div`
  width: 100%;
  margin-bottom: 0px;
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #2e3840;
`;

const Required = styled.span`
  color: #fe7156;
  margin-left: 2px;
`;

const Input = styled.input`
  flex: 1;
  height: 56px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasError ? "#FF4D4F" : "#e5e5e5")};
  padding: 0 16px;
  font-size: 16px;
  background-color: ${(props) => (props.disabled ? "#f5f5f5" : "white")};
  width: 100%;
  box-sizing: border-box;
  animation: ${(props) => (props.isShaking ? shakeAnimation : "none")} 0.5s;
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  letter-spacing: ${(props) => (props.isNumeric ? "2px" : "normal")};

  &::placeholder {
    color: #999;
    letter-spacing: normal;
  }

  &:focus {
    border-color: ${(props) => (props.hasError ? "#FF4D4F" : "#0075FF")};
    outline: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
`;

const MinorWarningText = styled.div`
  color: #ff4d4f;
  font-size: 13px;
  margin-bottom: 8px;
  font-weight: 500;
`;

const ActionButton = styled(motion.button)`
  width: 100%;
  display: flex;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 20px;
  background: ${(props) => (props.disabled ? "#A9DAFF" : "#3855E2")};
  color: ${(props) => (props.disabled ? "#CCEAFF" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  opacity: 1;
  pointer-events: auto;
`;

// 추가 스타일
const ErrorAlert = styled.div`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const NavigationButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-top: 0px;
  padding: 0 0px;
  background-color: #f8f8f8;
  margin: 0 auto;

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
    justify-content: center;
  }
`;

const SameAddressCheckbox = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
  font-size: 12px;
  color: #666;

  input {
    margin-right: 5px;
  }

  label {
    cursor: pointer;
    font-weight: normal;
  }
`;

// 토스트 알림 스타일
const Toast = styled(motion.div)`
  position: fixed;
  bottom: 30px;
  left: 5%;
  /* 데스크탑에서는 32%로 위치 조정 */
  @media (min-width: 768px) {
    left: 38.5%;
  }

  transform: translateX(-50%);
  background-color: rgba(46, 56, 64, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  z-index: 1000;
  min-width: 90%;
  max-width: 90%;
  text-align: left;
`;

const AlertIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #0075ff;
  color: white;
  border-radius: 50%;
  margin-right: 8px;
  font-size: 16px;
  font-weight: bold;
`;

const Toast2 = styled(motion.div)`
  position: fixed;
  top: 230px;
  left: 5%;
  /* 데스크탑에서는 32%로 위치 조정 */
  @media (min-width: 768px) {
    left: 3%;
    max-width: 200px;
  }

  transform: translateX(-50%);
  background-color: rgba(46, 56, 64, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  z-index: 1000;
  min-width: 90%;
  max-width: 90%;
  text-align: left;
`;

const AlertIcon2 = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: white;
  color: #0075ff;
  border-radius: 50%;
  margin-right: 8px;
  font-size: 12px;
  font-weight: bold;
`;

const BankSelect = styled(Select)`
  .select__control {
    height: 50px;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: none;
    background: white;

    &:hover {
      border-color: #0075ff;
    }

    &--is-focused {
      border-color: #0075ff;
      box-shadow: 0 0 0 1px #0075ff;
    }
  }

  .select__placeholder {
    color: #999;
  }

  .select__single-value {
    color: #333;
    margin: 0;
    padding: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  .select__input {
    color: #333 !important;

    input {
      color: #333 !important;
    }
  }

  .select__input-container {
    margin: 0;
    padding: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: #333;

    input {
      opacity: 0 !important;
      width: 0 !important;
      height: 0 !important;
      padding: 0 !important;
      border: none !important;
      position: absolute;
      color: #333 !important;

      &:focus {
        opacity: 1 !important;
        width: 100% !important;
        height: auto !important;
        position: relative;
      }
    }
  }

  .select__value-container {
    padding: 2px 16px;
    cursor: pointer;
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
  }

  .select__menu {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    margin-top: 8px;
    z-index: 100;
    position: absolute;
    width: 100%;
    background: white;
  }

  .select__menu-list {
    max-height: 300px;
    overflow-y: auto;
    padding: 8px 0;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;

      &:hover {
        background: #555;
      }
    }
  }

  .select__indicator-separator {
    display: none;
  }

  .select__clear-indicator {
    cursor: pointer;
    color: #999;

    &:hover {
      color: #666;
    }
  }

  .select__dropdown-indicator {
    color: #999;
    padding: 0 16px;

    &:hover {
      color: #666;
    }
  }
`;

const OptionContainer = styled.div`
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const SingleValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BankIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const ErrorText = styled.div`
  color: #ff4d4f;
  font-size: 13px;
  margin-top: 4px;
`;

export default CoOwnerDataCollection;
